import React, { useEffect } from 'react';
import LessonLayout from '../components/LessonLayout';
import { useTheme } from '../context/ThemeContext';
import { useUser } from '../context/UserContext';
import { Card, Row, Col, Alert, Button, Form } from 'react-bootstrap';

const ThemeDisplay = () => {
  const { theme, toggleTheme } = useTheme();
  console.log('🎨 ThemeDisplay: Current theme =', theme);

  return (
    <Card className="mb-3">
      <Card.Header>Theme Context Consumer</Card.Header>
      <Card.Body className={theme === 'dark' ? 'bg-dark text-white' : ''}>
        <h5>Current Theme: {theme}</h5>
        <Button onClick={toggleTheme} variant={theme === 'dark' ? 'light' : 'dark'}>
          Toggle Theme
        </Button>
      </Card.Body>
    </Card>
  );
};

const UserDisplay = () => {
  const { user, login, logout } = useUser();
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');

  console.log('👤 UserDisplay: Current user =', user);

  const handleLogin = () => {
    if (name && email) {
      login(name, email);
      setName('');
      setEmail('');
    }
  };

  return (
    <Card className="mb-3">
      <Card.Header>User Context Consumer</Card.Header>
      <Card.Body>
        {user.isAuthenticated ? (
          <div>
            <Alert variant="success">
              <strong>Logged in as:</strong> {user.name} ({user.email})
            </Alert>
            <Button onClick={logout} variant="danger">Logout</Button>
          </div>
        ) : (
          <div>
            <Form.Group className="mb-2">
              <Form.Control
                type="text"
                placeholder="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Control
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Form.Group>
            <Button onClick={handleLogin} variant="primary">Login</Button>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

const Lesson09_ContextAPI = () => {
  useEffect(() => {
    console.log('📖 Lesson 09: Context API - Loaded');
    console.log('🔄 Context allows sharing state without prop drilling');
  }, []);

  return (
    <LessonLayout 
      lessonNumber={9}
      title="Context API" 
      description="Share state across components without prop drilling"
    >
      <Alert variant="success">
        <strong>What You'll Learn:</strong>
        <ul className="mb-0 mt-2">
          <li>Creating React Context</li>
          <li>Provider component pattern</li>
          <li>Consuming context with useContext hook</li>
          <li>When to use Context vs Props</li>
        </ul>
      </Alert>

      <Row className="mt-4">
        <Col md={6}>
          <ThemeDisplay />
        </Col>
        <Col md={6}>
          <UserDisplay />
        </Col>
      </Row>

      <Card className="mt-3">
        <Card.Header>How Context API Works</Card.Header>
        <Card.Body>
          <pre className="code-example">
{`// 1. Create Context
const ThemeContext = createContext();

// 2. Create Provider
export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState('light');
  
  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// 3. Use Context in Components
const MyComponent = () => {
  const { theme, setTheme } = useContext(ThemeContext);
  return <div>Theme: {theme}</div>;
};`}
          </pre>
        </Card.Body>
      </Card>

      <Alert variant="warning" className="mt-4">
        <strong>💡 When to Use Context:</strong>
        <ul className="mb-0">
          <li>Theme data (dark mode / light mode)</li>
          <li>User authentication state</li>
          <li>Language/locale preferences</li>
          <li>Sharing data across many components at different nesting levels</li>
        </ul>
      </Alert>
    </LessonLayout>
  );
};

export default Lesson09_ContextAPI;
